import { Grid, Text, cx } from '@vtex/admin-ui'
import { forwardRef } from 'react'
import { useIntl } from 'react-intl'
import { Event, type DefaultProps } from 'typings'
import { messages } from './messages'
import styles from './styles'
import { useAnalytics } from 'use-analytics'

export * from './DefaultActions'

export enum CONNECTOR {
  AND = 'and',
  OR = 'or',
  WITH = 'with',
  NONE = '',
}

type RuleActionsType = DefaultProps & {
  readonly?: boolean
}

export function RuleStack(props: DefaultProps) {
  return (
    <div data-pp-border className={styles.stack}>
      {props.children}
    </div>
  )
}

export function Rule(
  props: DefaultProps & {
    connector?: CONNECTOR
    onClick?: (e: React.MouseEvent) => void
  }
) {
  const { children, connector = CONNECTOR.AND, onClick, ...rest } = props
  const { $t } = useIntl()
  const { track } = useAnalytics()

  const hasConnector = connector !== CONNECTOR.NONE

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      track(Event.CARD_EDIT_CLICK)
      onClick(e)
    }
  }

  return (
    <Grid
      templateColumns={
        '[icon] auto [title] minmax(0, 1fr) [rules] minmax(0, 2fr) [actions] auto'
      }
      className={cx(styles.rule, 'rule-item')}
      data--condition-connector={
        hasConnector ? $t(messages.connector[connector]) : ''
      }
      data-clickable={!!onClick}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </Grid>
  )
}

export function RuleIcon(props: DefaultProps & { className?: string }) {
  const { children, className = '' } = props

  return <div className={cx(styles.icon, className)}>{children}</div>
}

export function RuleDescription(props: DefaultProps) {
  return (
    <Text tone="secondary" className={styles.description}>
      {props.children}
    </Text>
  )
}

export const RuleContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    fluid?: boolean
  }
>(function RuleContent(props, ref) {
  const { fluid, children, className = '', ...rest } = props

  return (
    <div
      className={cx(styles.content, className)}
      data-fluid={fluid}
      ref={ref}
      data-testid="rule-content"
      {...rest}
    >
      {children}
    </div>
  )
})

export function RuleActions(props: RuleActionsType & { className?: string }) {
  const { readonly, className = '' } = props

  if (readonly) return null
  return (
    <div className={cx(styles.actions, className, 'actions')}>
      {props.children}
    </div>
  )
}

export const ruleStackMessages = messages
